/**
 * Compresses image to base64 with max size limit
 * @param {File} file - Image file to compress
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxHeight - Maximum height in pixels
 * @param {number} quality - JPEG quality (0-1)
 * @param {number} maxSizeKB - Maximum output size in KB
 * @returns {Promise<string>} Base64 data URL of compressed image
 */
export const compressImage = (
  file,
  maxWidth = 300,
  maxHeight = 300,
  quality = 0.8,
  maxSizeKB = 100,
) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) {
      reject(new Error("Invalid file type. Please select an image."));
      return;
    }

    // Check file size first (before processing)
    if (file.size > maxSizeKB * 1024 * 5) {
      // Allow up to 5x maxSizeKB before compression
      reject(
        new Error(
          `Image is too large. Maximum size is ${maxSizeKB * 5}KB before compression.`,
        ),
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }

        // Create canvas and compress
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 with quality setting
        let base64 = canvas.toDataURL("image/jpeg", quality);

        // If still too large, reduce quality further
        let attempts = 0;
        while (
          base64.length > maxSizeKB * 1024 &&
          attempts < 5 &&
          quality > 0.3
        ) {
          quality -= 0.1;
          base64 = canvas.toDataURL("image/jpeg", quality);
          attempts++;
        }

        if (base64.length > maxSizeKB * 1024) {
          reject(
            new Error(
              `Image could not be compressed below ${maxSizeKB}KB. Please use a smaller image.`,
            ),
          );
          return;
        }

        resolve(base64);
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};
